/**
 * Where a PDF compile stands while the preview waits for it (API: CompileSlots, 27 Oct).
 *
 * The server runs a fixed number of compiles at once; the rest wait in line. A
 * PDF request carries a ticket of the preview's own making, and the preview
 * asks GET /api/compile/place with it, so a wait reads "Position 3 in line"
 * instead of a spinner with no end. When every compiler stayed busy for a
 * minute the server answers 503 "compile_busy" with how long to wait, and the
 * preview tries again by itself.
 */

import { useEffect, useState } from "react";
import { API_BASE } from "@/contexts/ApiContext";

export const COMPILE_TICKET_HEADER = "X-Compile-Ticket";

/** How many busy answers in a row the preview waits through before showing the error. */
export const MAX_BUSY_RETRIES = 6;

/** A compile that answers within this long never shows a place in line. */
const FIRST_ASK_MS = 1500;
const ASK_EVERY_MS = 2000;

type FetchWithAuth = (url: string, options?: RequestInit) => Promise<Response>;

export function newCompileTicket(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** Seconds to wait when the server said every compiler is busy, else null. */
export async function busyRetryAfter(response: Response): Promise<number | null> {
  if (response.status !== 503) return null;
  try {
    const body = await response.clone().json();
    if (body?.error !== "compile_busy") return null;
    const seconds = Number(body.retryAfterSeconds);
    return Number.isFinite(seconds) && seconds > 0 ? seconds : 5;
  } catch {
    return null;
  }
}

/** Resolves after `ms`, or rejects with an AbortError as soon as `signal` fires. */
export function waitFor(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new DOMException("Aborted", "AbortError"));
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

/**
 * The ticket's place in line while `ticket` is set: 1 = next, 0 = compiling,
 * null = not known (not asked yet, finished, or the question failed).
 */
export function useCompilePlace(ticket: string | null, fetchWithAuth: FetchWithAuth): number | null {
  const [place, setPlace] = useState<number | null>(null);

  useEffect(() => {
    setPlace(null);
    if (!ticket) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;

    const ask = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_BASE}/compile/place?ticket=${encodeURIComponent(ticket)}`,
        );
        if (response.ok) {
          const body = await response.json();
          if (!stopped) setPlace(typeof body?.place === "number" ? body.place : null);
        }
      } catch {
        // A place in line is a courtesy; the PDF request itself is what matters.
      }
      if (!stopped) timer = setTimeout(ask, ASK_EVERY_MS);
    };
    timer = setTimeout(ask, FIRST_ASK_MS);

    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [ticket, fetchWithAuth]);

  return place;
}
